/**
 * Components that understand Pressable's `pending`: Pressable and the button
 * atoms built on it (Button, IconButton, FabButton). A pending Pressable's
 * `asChild` passes `pending` on to such a child, so it blocks its own handlers
 * and timers too. Any other child only loses the activation props it was given
 * (`PENDING_STRIPPED_PROPS`); handlers it creates internally still run.
 */
const PENDING_AWARE = Symbol.for("luce.pendingAware");

export function markPendingAware<T extends object>(component: T): T {
  (component as Record<symbol, unknown>)[PENDING_AWARE] = true;
  return component;
}

export function isPendingAware(type: unknown): boolean {
  return (
    (typeof type === "object" || typeof type === "function") &&
    type !== null &&
    (type as Record<symbol, unknown>)[PENDING_AWARE] === true
  );
}

/**
 * Activation props, cleared on a disabled or pending `asChild` child that
 * doesn't understand `pending`: Slot runs the child's handlers before the
 * parent's, so the parent's own guard can't stop them. Releases (press out,
 * pointer up, key up) are kept.
 */
export const PENDING_STRIPPED_PROPS = {
  onClick: undefined,
  onLongClick: undefined,
  onPointerDown: undefined,
  onKeyDown: undefined,
  onPress: undefined,
  onPressIn: undefined,
  onLongPress: undefined,
  onAccessibilityAction: undefined,
  onAccessibilityTap: undefined,
  onMagicTap: undefined,
} as const;
